import type { SVGAttributes } from 'react';
import { PortIconSVG } from 'atto-svgs';
import React from 'react';

import { FocusRing } from '../../common/focus_rings';
import { colors, darkThemeSelector, styled } from '../../stitches.config';

export type OutletConnector = 'iec-c13' | 'iec-c14' | 'nema-5-15';

const outletIcons: Record<OutletConnector, React.ComponentType<SVGAttributes<SVGSVGElement>>> = {
  'iec-c13': PortIconSVG.IEC_C13,
  // No C14 art of its own yet; the C13 drawing stands in.
  'iec-c14': PortIconSVG.IEC_C13,
  'nema-5-15': PortIconSVG.NEMA_5_15,
};

const outletValues = {
  zIndex: {
    representation: 0,
    contents: 1,
  },
};

/**
 * The glyph box per variant, both on the asset's 6:5 ratio.
 *
 * The redrawn outlet is authored at 24×20 with a 1-unit border, so `detailed` draws it 1:1 and that
 * border lands on a whole pixel; `simple` is an exact 0.75× of it. They are the boxes `Port` uses,
 * which is the point — an outlet and a port are now the same size on a faceplate. The old art was
 * four files at four sizes (40×36 and 32×36 detailed, 22×20 and 18×20 simple), no two of them
 * sharing a ratio, each squashed into a box that didn't match.
 */
const outletSVGSizing = {
  detailed: {
    width: '$24',
    height: '$20',
  },
  simple: {
    width: '$18',
    height: '$15',
  },
};

const OutletSVG = styled('svg', {
  position: 'absolute',
  zIndex: outletValues.zIndex.representation,
  top: '50%',
  left: '50%',
  display: 'flex',
  overflow: 'visible',
  /*
   * One colour, three opacities — the asset paints its own border as a solid 1-unit ring, its body at
   * 25%, and its ground and blade slots at 50%, all `currentColor`. So this is the saturated stroke
   * token rather than the pale background one: at `hardwareBg*` the ring would come out `green100`
   * and all but disappear on a white card, and the body behind it would be a quarter of that.
   *
   * `stroke`/`strokeWidth`/`paintOrder` used to sit here because the old art was a single flat shape
   * with no border of its own. A stroke now traces every layer the asset already draws.
   */
  color: '$$outletStrokeColor',

  variants: {
    /*
     * The ground slot sits at the top of the asset, so `top` draws it as authored and the others are
     * quarter turns from there. One drawing rotated four ways replaces the old Horizontal/Vertical
     * pair, which were two separate traces of the same outlet kept in sync by hand.
     */
    orientation: {
      top: {
        transform: 'translate(-50%, -50%)',
      },
      right: {
        transform: 'translate(-50%, -50%) rotate(90deg)',
      },
      bottom: {
        transform: 'translate(-50%, -50%) rotate(180deg)',
      },
      left: {
        transform: 'translate(-50%, -50%) rotate(270deg)',
      },
    },
    variant: {
      detailed: {
        width: outletSVGSizing.detailed.width,
        height: outletSVGSizing.detailed.height,
      },
      simple: {
        width: outletSVGSizing.simple.width,
        height: outletSVGSizing.simple.height,
      },
    },
  },
});
OutletSVG.displayName = 'OutletSVG';

const OutletNumber = styled('span', {
  position: 'relative',
  zIndex: outletValues.zIndex.contents,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: '100%',
  height: '100%',
  fontFamily: '$mono',
  fontSize: '$10',
  lineHeight: '$12',
  color: '$$outletContentsColor',
});
OutletNumber.displayName = 'OutletNumber';

const OutletContainer = styled('div', FocusRing, {
  position: 'relative',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',

  variants: {
    /*
     * The radius is half the short side, so the focus ring traces the asset's capsule rather than a
     * rounder box around it — and it stays true when a quarter turn swaps which side is shorter,
     * because the shorter one is 20 either way. `simple` wants 7.5 and the radii scale has no such
     * step; `$8` overshoots, the browser scales it back down to 7.5 to fit the 15px side, so the
     * result is the same. Don't "correct" it to `$6`.
     */
    variant: {
      detailed: {
        widthAll: outletSVGSizing.detailed.width,
        heightAll: outletSVGSizing.detailed.height,
        borderRadius: '$10',
      },
      simple: {
        widthAll: outletSVGSizing.simple.width,
        heightAll: outletSVGSizing.simple.height,
        borderRadius: '$8',
      },
    },
    orientation: {
      top: {},
      right: {},
      bottom: {},
      left: {},
    },
    connector: {
      'iec-c13': {},
      'iec-c14': {},
      'nema-5-15': {},
    },
    /*
     * Named `tone` because an outlet already spends `variant` on its size. No `$$outletBgColor`: the
     * asset derives its own fill from `$$outletStrokeColor` at 25%, so the `hardwareBg*` family no
     * longer reaches an outlet.
     */
    tone: {
      disabled: {
        $$outletContentsColor: colors.hardwareContentDisabledLight,
        $$outletStrokeColor: colors.hardwareStrokeDisabledLight,

        [darkThemeSelector]: {
          $$outletContentsColor: colors.hardwareContentDisabledDark,
          $$outletStrokeColor: colors.hardwareStrokeDisabledDark,
        },
      },
      negative: {
        $$outletContentsColor: colors.hardwareContentNegativeLight,
        $$outletStrokeColor: colors.hardwareStrokeNegativeLight,

        [darkThemeSelector]: {
          $$outletContentsColor: colors.hardwareContentNegativeDark,
          $$outletStrokeColor: colors.hardwareStrokeNegativeDark,
        },
      },
      neutral: {
        $$outletContentsColor: colors.hardwareContentNeutralLight,
        $$outletStrokeColor: colors.hardwareStrokeNeutralLight,

        [darkThemeSelector]: {
          $$outletContentsColor: colors.hardwareContentNeutralDark,
          $$outletStrokeColor: colors.hardwareStrokeNeutralDark,
        },
      },
      positive: {
        $$outletContentsColor: colors.hardwareContentPositiveLight,
        $$outletStrokeColor: colors.hardwareStrokePositiveLight,

        [darkThemeSelector]: {
          $$outletContentsColor: colors.hardwareContentPositiveDark,
          $$outletStrokeColor: colors.hardwareStrokePositiveDark,
        },
      },
    },
  },

  compoundVariants: [
    // IEC connectors are chamfered boxes rather than capsules, so the focus ring follows the corners.
    {
      connector: 'iec-c13',
      css: {
        borderRadius: '$4',
      },
    },
    {
      connector: 'iec-c14',
      css: {
        borderRadius: '$4',
      },
    },
    // A quarter turn swaps the footprint: the box becomes the asset's height by its width.
    {
      orientation: 'right',
      variant: 'detailed',
      css: {
        widthAll: outletSVGSizing.detailed.height,
        heightAll: outletSVGSizing.detailed.width,
      },
    },
    {
      orientation: 'left',
      variant: 'detailed',
      css: {
        widthAll: outletSVGSizing.detailed.height,
        heightAll: outletSVGSizing.detailed.width,
      },
    },
    {
      orientation: 'right',
      variant: 'simple',
      css: {
        widthAll: outletSVGSizing.simple.height,
        heightAll: outletSVGSizing.simple.width,
      },
    },
    {
      orientation: 'left',
      variant: 'simple',
      css: {
        widthAll: outletSVGSizing.simple.height,
        heightAll: outletSVGSizing.simple.width,
      },
    },
  ],
});
OutletContainer.displayName = 'OutletContainer';

export type OutletOrientation = 'top' | 'right' | 'bottom' | 'left';
export type OutletStatus = 'connected' | 'disconnected';
export type OutletVariant = 'detailed' | 'simple';

export type OutletProps = {
  /**
   * The connector drawn: a `nema-5-15` wall outlet (default), the `iec-c13` socket on a PDU, or the
   * `iec-c14` inlet on a power supply.
   */
  connector?: OutletConnector;
  /**
   * The outlet is functionally disabled. Takes precedence over `error` and `status`.
   */
  disabled?: boolean;
  /**
   * The outlet has an issue.
   */
  error?: boolean;
  /**
   * The outlet number, displayed on the `simple` variant.
   */
  number?: number;
  /**
   * The function to call when the outlet is clicked.
   */
  onClick?: (event: any) => void;
  /**
   * Which way the outlet faces — its ground slot points at the side you name. `right` and `left` are
   * quarter turns, so they swap the footprint.
   */
  orientation?: OutletOrientation;
  /**
   * Whether something is drawing power from the outlet.
   */
  status?: OutletStatus;
  /**
   * The outlet variant, either `detailed` or the smaller `simple` that carries its number.
   */
  variant?: OutletVariant;
};

type OutletTone = 'disabled' | 'negative' | 'neutral' | 'positive';

/**
 * One tone per outlet, resolved once.
 *
 * Colour used to be picked twice — content on the contents wrapper, fill and stroke on the SVG — from
 * `compoundVariants` listed in different orders, so an outlet that was both `disabled` and `error`
 * came out disabled in its number and negative in its shape. `disabled` wins outright.
 */
function getOutletTone(disabled: boolean, error: boolean, status?: OutletStatus): OutletTone {
  if (disabled) return 'disabled';
  if (error) return 'negative';
  if (status === 'connected') return 'positive';
  return 'neutral';
}

/** What each tone says in words. `neutral` is an outlet with nothing drawing from it. */
const OUTLET_TONE_LABELS: Record<OutletTone, string> = {
  disabled: 'Disabled',
  negative: 'Error',
  neutral: 'Disconnected',
  positive: 'Connected',
};

/**
 * What a screen reader announces for an outlet that is also a button.
 *
 * Keyed off the resolved tone rather than the props it came from, so the name can't claim one state
 * while the colour draws another — the drift `getOutletTone` exists to stop. `detailed` is the
 * variant a faceplate uses and it draws no number at all, so without this the control has no text.
 *
 * See `getPortAccessibleName`, which does the same job for the glyph beside it.
 */
function getOutletAccessibleName(number: number | undefined, tone: OutletTone): string {
  const socket = number === undefined ? 'Outlet' : `Outlet ${number}`;

  return `${socket}, ${OUTLET_TONE_LABELS[tone]}`;
}

/**
 * A power outlet on a PDU faceplate.
 *
 * Draws the same redrawn 24×20 art as `Port`, in the same three `currentColor` layers, so the two
 * read as one system — see `outletSVGSizing` for how the boxes line up.
 */
export function Outlet({
  connector = 'nema-5-15',
  disabled = false,
  error = false,
  number,
  onClick,
  orientation = 'top',
  status,
  variant = 'detailed',
}: OutletProps) {
  const tone = getOutletTone(disabled, error, status);

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (onClick && (event.key === 'Enter' || event.key === ' ')) {
      event.preventDefault();
      onClick(event);
    }
  };

  return (
    <OutletContainer
      // Everything that makes this a button is applied together: without a role the element is
      // generic and may not carry `aria-label`, and without the key handler it is a button that
      // only a mouse can press.
      {...(onClick
        ? {
            'aria-label': getOutletAccessibleName(number, tone),
            role: 'button',
            tabIndex: 0,
            onClick,
            onKeyDown: handleKeyDown,
          }
        : {})}
      connector={connector}
      orientation={orientation}
      tone={tone}
      variant={variant}
    >
      {number !== undefined && variant === 'simple' && <OutletNumber>{number}</OutletNumber>}
      <OutletSVG as={outletIcons[connector]} orientation={orientation} variant={variant} />
    </OutletContainer>
  );
}
